import React, {useEffect, useState} from 'react';
import {Button, Col, Form, Input, message, Modal, Row, Typography} from "antd";
import styled from "styled-components";
import {useAppDispatch, useAppSelector} from "../../../store/store";
import {useNavigate} from "react-router";
import {useSearchParams} from "react-router-dom";
import {clearLogin, login} from "../../../store/reducers/authReducer/authReducer";
import {ForgotPasswordModal} from "./components/ForgotPasswordModal";
import Logo from '../../../media/icons/logo.svg?react';
import {CustomCard} from "../../../components/UI/AntCustom/CustomCard";
import {PageWrapper} from "../../../components/UI/PageWrapper/PageWrapper";
import {pathFromRoute} from "../../../utils/helpers/acl/pathFromRoute";
import {roles} from '../../../config/acl/roles';
import {Role} from "../../../config/acl/roles/Role";
import {Route} from "../../../config/acl/routes/Route";
import {useIsMobile} from "../../../hooks/useWindowSize";
import {defineMessages, useIntl} from "react-intl";
import {CommonFormMessages} from "../../../intl/commonMessages/CommonFormMessages";
import {CustomFieldInput} from "../../../components/UI/AntCustom/CustomFieldInput";
import {CustomButton} from "../../../components/UI/AntCustom/CustomButton";
import {PasswordInput} from "../../../components/UI/AntCustom/PasswordInput";
import {onFinishTrimmed} from "../../../utils/helpers/formHelpers/onFinishTrimmed";
import {shouldShowForceLogoutModal} from "../../../websocket/handleForceLogout";
import {
  buildMobileAuthCallbackUrl,
  isInAppWebView,
  isMobileAuthCallbackRequest,
} from "../../../utils/constants/mobileAppAuth";
import {AuthPageCard, AuthFormWrapper} from "../auth/AuthPageStyles";
import {mediaScreenSizes} from "../../../utils/constans/styles/screenSizes";

const LoginPageCard = styled(AuthPageCard)`
  @media (max-width: ${mediaScreenSizes.mobile}) {
    .ant-form-item {
      margin-bottom: 40px;
    }
  }
`;


const LoginPageMessages = defineMessages({
  title: {
    id: "loginPage.title",
    defaultMessage: "Log in to access your Jetsnode account.",
  },
  login: {
    id: "loginPage.login",
    defaultMessage: "Log in",
  },
  forgotPassword: {
    id: "loginPage.forgotPassword",
    defaultMessage: "Forgot password?",
  },
  toRegister: {
    id: "loginPage.toRegister",
    defaultMessage: "Still don't have an account?",
  },
})

interface ILoginFormData {
  login: string,
  password: string,
}

export const LoginPage = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isForceLogoutModalVisible, setIsForceLogoutModalVisible] = useState<boolean>(false);
  const isMobile = useIsMobile();

  const {
    isAuthenticated,
    isLoginLoading,
    isLoginSuccess,
    loginErrorMessage,
    role,
  } = useAppSelector(state => state.auth);

  useEffect(() => {
    // Check if we need to show force logout modal
    if (shouldShowForceLogoutModal()) {
      setIsForceLogoutModalVisible(true);
    }
    
    return () => {
      dispatch(clearLogin());
    }
  }, [dispatch])

  useEffect(() => {
    if (!isLoginLoading && loginErrorMessage)
      message.error(loginErrorMessage);
  }, [isLoginLoading, isLoginSuccess, loginErrorMessage])

  const onFinish = (values: ILoginFormData) => {
    dispatch(login({
      login: values.login,
      password: values.password,
    }));
  }

  const onFinishFailed = () => {
    message.error(intl.formatMessage(CommonFormMessages.incorrect))
  }

  const initialValues: ILoginFormData = {
    login: '',
    password: '',
  }
  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleForceLogoutModalClose = () => {
    setIsForceLogoutModalVisible(false);
  };

  if (isAuthenticated) {
    const shouldRedirectToApp = isMobileAuthCallbackRequest(searchParams) || isInAppWebView();
    if (shouldRedirectToApp) {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      if (accessToken && refreshToken) {
        window.location.href = buildMobileAuthCallbackUrl(accessToken, refreshToken);
        return null;
      }
    }
    const targetRoute =
      isMobile && role === Role.chartererAdmin
        ? Route.newTripRequest
        : roles[role].homeRoute;
    navigate(pathFromRoute(targetRoute), {replace: true});
    return null;
  }

  return (
    <>
      {!isModalVisible &&
          <PageWrapper justifyContent={'center'}>
              <LoginPageCard $maxWidth={400} $formAtBottom={true}>
              <CustomCard title={<span className="auth-logo-wrap"><Logo/></span>} width={'max'} isContainerCard={true} maxWidth={400} titlePosition={'center'}>
                <AuthFormWrapper>
                  <Typography className="auth-subtitle">{intl.formatMessage(LoginPageMessages.title)}</Typography>
                  <Form
                      form={form}
                      onFinish={(values) => onFinishTrimmed(values, onFinish)}
                      onFinishFailed={onFinishFailed}
                      layout={'vertical'}
                      initialValues={initialValues}
                  >
                      <CustomFieldInput
                          label={intl.formatMessage(CommonFormMessages.emailLabel)}
                          name={'login'}
                          rules={[
                            {required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
                            {type: 'email', message: intl.formatMessage(CommonFormMessages.ruleIncorrectEmail)}
                          ]}
                      >
                          <Input placeholder={intl.formatMessage(CommonFormMessages.emailPlaceholder)}/>
                      </CustomFieldInput>

                      <CustomFieldInput
                          label={intl.formatMessage(CommonFormMessages.passwordLabel)}
                          name={'password'}
                          rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
                      >
                          <PasswordInput placeholder={intl.formatMessage(CommonFormMessages.passwordPlaceholder)}/>
                      </CustomFieldInput>

                      <CustomFieldInput className="auth-submit-wrap" style={{marginBottom: 12}}>
                          <CustomButton
                              block
                              heightSize={'m'}
                              type={'primary'}
                              htmlType={'submit'}
                              loading={isLoginLoading}
                          >
                            {intl.formatMessage(LoginPageMessages.login)}
                          </CustomButton>
                      </CustomFieldInput>

                    <Row className="auth-actions-row" justify={"space-between"}>
                      <Col>
                        <Button type={'link'} onClick={showModal}>{intl.formatMessage(LoginPageMessages.forgotPassword)}</Button>
                      </Col>
                      <Col>
                        <Button type={'link'} onClick={() => navigate(pathFromRoute(Route.register))}>{intl.formatMessage(LoginPageMessages.toRegister)}</Button>
                      </Col>
                    </Row>
                  </Form>
                </AuthFormWrapper>
              </CustomCard>
              </LoginPageCard>
          </PageWrapper>
      }

      {isModalVisible && <ForgotPasswordModal isModalVisible={isModalVisible} setIsModalVisible={setIsModalVisible}/>}

      <Modal
        title={intl.formatMessage({id: 'forceLogout.title', defaultMessage: 'Session ended'})}
        open={isForceLogoutModalVisible}
        onOk={handleForceLogoutModalClose}
        centered
        okText="OK"
        maskClosable={false}
        closable={true}
        footer={[
          <Button key="ok" type="primary" onClick={handleForceLogoutModalClose}>
            OK
          </Button>
        ]}
      >
        <p>{intl.formatMessage({id: 'forceLogout.defaultMessage', defaultMessage: 'Your account allows only one active logged-in session at a time.'})}</p>
      </Modal>

    </>

  );
};

